import {
	API_ENDPOINTS,
	Button,
	ButtonTypes,
	Card,
	CardBody,
	CardData,
	customToast,
	FormInput,
	Modal,
	PageLayout,
	route,
	SelectOption,
	Spinner,
	StringHelpers,
	SubTitle,
	useLangContext,
	useQuery,
	Variants,
	WidthConstrainedContainer,
} from 'carrier-fe';
import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
	MergeReviewCompany,
	MergeReviewResponse,
} from '../../../types/companies/merge';

function CompanyMerge() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { crud } = useLangContext();

	const companyIds = useMemo(() => {
		const raw = searchParams.get('companies') || '';
		return Array.from(
			new Set(
				raw
					.split(',')
					.map((id) => id.trim())
					.filter(Boolean)
			)
		);
	}, [searchParams]);

	const validSelection = companyIds.length === 2;

	useEffect(() => {
		if (!validSelection) {
			customToast({
				title:
					crud?.buttons?.merge?.select_companies_to_merge ||
					'Please select exactly 2 companies to merge',
				variant: Variants.Warning,
			});
			navigate('/companies', { replace: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [validSelection]);

	if (!validSelection) {
		return null;
	}

	return <CompanyMergeContent companyIds={companyIds} />;
}

function CompanyMergeContent({ companyIds }: { companyIds: string[] }) {
	const navigate = useNavigate();
	const { crud, fields } = useLangContext();

	const [survivingCompanyId, setSurvivingCompanyId] = useState<string>('');
	const [primaryIndividualId, setPrimaryIndividualId] = useState<string>('');
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const reviewUrl =
		route(API_ENDPOINTS.ADMIN.COMPANY.MERGE_REVIEW) +
		`?company_ids[]=${encodeURIComponent(companyIds[0])}` +
		`&company_ids[]=${encodeURIComponent(companyIds[1])}`;

	const { data, isLoading, error } = useQuery<{ data: MergeReviewResponse }>(
		reviewUrl
	);

	useEffect(() => {
		if (error) {
			customToast({
				title:
					(error.response?.data as any)?.message ||
					'An error occurred. Please try again.',
				variant: Variants.Danger,
			});
			navigate('/companies', { replace: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error]);

	const companies = data?.data?.companies || [];
	const individuals = data?.data?.individuals || [];

	const survivingCompany = companies.find(
		(company) => company.id === survivingCompanyId
	);
	const oldCompany = companies.find(
		(company) => company.id !== survivingCompanyId
	);
	const primaryIndividual = individuals.find(
		(individual) => individual.id === primaryIndividualId
	);

	const individualOptions: SelectOption[] = useMemo(
		() =>
			individuals.map((individual) => {
				const companyName = companies.find(
					(company) => company.id === individual.company_id
				)?.name;

				return {
					label:
						individual.full_name +
						(individual.user_email || individual.email
							? ` (${individual.user_email || individual.email})`
							: '') +
						(companyName ? ` - ${companyName}` : '') +
						(individual.has_user
							? ''
							: ` - ${
									fields?.no_user_account ||
									'no user account'
							  }`),
					value: individual.id,
					disabled: !individual.has_user,
				};
			}),
		[individuals, companies, fields]
	);

	const canConfirm =
		!!survivingCompanyId && !!primaryIndividualId && !submitting;

	const handleMerge = async () => {
		if (!survivingCompany || !oldCompany) {
			return;
		}

		setSubmitting(true);

		try {
			const response = await axios.post(
				route(API_ENDPOINTS.ADMIN.COMPANY.MERGE),
				{
					old_company_id: oldCompany.id,
					new_company_id: survivingCompany.id,
					primary_individual_id: primaryIndividualId,
				}
			);

			customToast({
				title:
					response.data?.message ||
					'Companies have been merged successfully.',
				variant: Variants.Success,
			});
			navigate('/companies');
		} catch (error: any) {
			customToast({
				title:
					error?.response?.data?.message ||
					'An error occurred. Please try again.',
				variant: Variants.Danger,
			});
			setConfirmModalOpen(false);
		} finally {
			setSubmitting(false);
		}
	};

	const renderCompanyCard = (company: MergeReviewCompany) => {
		const isSurvivor = company.id === survivingCompanyId;
		const addressParts = [
			company.address?.address_line_1,
			company.address?.town_city,
			company.address?.postcode_zipcode,
		].filter(Boolean);

		const enabledSystems = [
			company.gas_boiler_systems_enabled &&
				(fields?.gas_boiler_systems_enabled || 'Gas boiler'),
			company.heat_pump_systems_enabled &&
				(fields?.heat_pump_systems_enabled || 'Heat pump'),
			company.solar_systems_enabled &&
				(fields?.solar_systems_enabled || 'Solar'),
			company.other_systems_enabled &&
				(fields?.other_systems_enabled || 'Other'),
		].filter(Boolean);

		return (
			<div
				className="col-12 col-md-6 mb-4"
				key={company.id}
			>
				<Card>
					<CardBody>
						<div className="d-flex justify-content-between align-items-start">
							<SubTitle
								title={StringHelpers.title(company.name)}
								style={{ color: '#464C5E' }}
							/>
							{!!survivingCompanyId && (
								<span
									className={`badge ${
										isSurvivor
											? 'text-bg-success'
											: 'text-bg-danger'
									}`}
								>
									{isSurvivor
										? fields?.company_will_be_kept ||
										  'Will be kept'
										: fields?.company_will_be_merged ||
										  'Will be merged & deleted'}
								</span>
							)}
						</div>
						<div className="row">
							<CardData.Column>
								<CardData.ColumnItem
									title={StringHelpers.title(
										fields?.type || 'Type'
									)}
									value={StringHelpers.title(
										company.type || '-'
									)}
								/>
								<CardData.ColumnItem
									title={StringHelpers.title(
										fields?.registration_state ||
											'Registration state'
									)}
									value={StringHelpers.title(
										company.registration_status || '-'
									)}
								/>
								<CardData.ColumnItem
									title={StringHelpers.title(
										fields?.address || 'Address'
									)}
									value={
										addressParts.length
											? addressParts.join(', ')
											: '-'
									}
								/>
								<CardData.ColumnItem
									title={StringHelpers.title(
										fields?.individuals || 'Individuals'
									)}
									value={String(company.individuals_count)}
								/>
								<CardData.ColumnItem
									title={StringHelpers.title(
										fields?.installations ||
											'Installations'
									)}
									value={String(company.installations_count)}
								/>
								<CardData.ColumnItem
									title={StringHelpers.title(
										fields?.enabled_systems ||
											'Enabled systems'
									)}
									value={
										enabledSystems.length
											? enabledSystems.join(', ')
											: '-'
									}
								/>
							</CardData.Column>
						</div>
						<Button
							label={
								isSurvivor
									? StringHelpers.title(
											fields?.keeping_this_company ||
												'Keeping this company'
									  )
									: StringHelpers.title(
											fields?.keep_this_company ||
												'Keep this company'
									  )
							}
							onClick={() => setSurvivingCompanyId(company.id)}
							variant={
								isSurvivor ? Variants.Success : Variants.Dark
							}
							type={
								isSurvivor
									? ButtonTypes.Solid
									: ButtonTypes.Outline
							}
							disabled={submitting}
						/>
					</CardBody>
				</Card>
			</div>
		);
	};

	return (
		<PageLayout
			title={StringHelpers.title(
				fields?.merge_companies || 'Merge Companies'
			)}
		>
			<WidthConstrainedContainer>
				{isLoading || !data ? (
					<div className="d-flex justify-content-center align-items-center vh-100">
						<Spinner />
					</div>
				) : (
					<>
						<div className="mb-4 d-flex justify-content-between align-items-center">
							<Button
								label={StringHelpers.title(
									crud?.buttons?.back?.default || 'Back'
								)}
								onClick={() => navigate('/companies')}
								variant={Variants.Dark}
								type={ButtonTypes.Outline}
								disabled={submitting}
							/>
						</div>

						<div className="mb-4">
							<p>
								{fields?.merge_companies_direction_hint ||
									'Choose which company should be kept. The other company will be merged into it and deleted.'}
							</p>
						</div>

						<div className="row">
							{companies.map((company) =>
								renderCompanyCard(company)
							)}
						</div>

						<Card>
							<CardBody>
								<SubTitle
									title={StringHelpers.title(
										fields?.primary_individual ||
											'Primary individual'
									)}
									className="mb-3"
									style={{ color: '#464C5E' }}
								/>
								<p>
									{fields?.merge_companies_primary_hint ||
										'Select the individual who will become the primary contact (and company manager) of the merged company. Individuals without a user account cannot be selected.'}
								</p>
								<FormInput
									type="select"
									name="primary_individual_id"
									label={StringHelpers.title(
										fields?.primary_individual ||
											'Primary individual'
									)}
									placeholder={
										fields?.select_primary_individual ||
										'Please select a primary individual'
									}
									value={primaryIndividualId}
									onChange={(value) =>
										setPrimaryIndividualId(
											String(value ?? '')
										)
									}
									options={individualOptions}
									disabled={submitting}
								/>

								<div className="mt-4 d-flex justify-content-end">
									<Button
										label={StringHelpers.title(
											crud?.buttons?.merge?.default ||
												'Merge'
										)}
										onClick={() =>
											setConfirmModalOpen(true)
										}
										variant={Variants.Danger}
										disabled={!canConfirm}
									/>
								</div>
							</CardBody>
						</Card>
					</>
				)}
			</WidthConstrainedContainer>

			<Modal
				open={confirmModalOpen}
				onClose={() => {
					if (!submitting) {
						setConfirmModalOpen(false);
					}
				}}
				title={StringHelpers.title(
					crud?.modals?.merge?.title || 'Merge companies?'
				)}
				confirmText={
					submitting
						? crud?.buttons?.merge?.submitting || 'Merging...'
						: crud?.buttons?.merge?.default || 'Merge'
				}
				closeText={crud?.buttons?.cancel?.default || 'Cancel'}
				onConfirm={handleMerge}
				disabled={submitting || !canConfirm}
				variant={Variants.Danger}
			>
				<p>
					{`"${oldCompany?.name || '-'}" ` +
						(fields?.merge_companies_confirm_into ||
							'will be merged into') +
						` "${survivingCompany?.name || '-'}".`}
				</p>
				<ul>
					<li>
						{`${oldCompany?.individuals_count ?? 0} ` +
							(fields?.merge_companies_confirm_individuals ||
								'individual(s) will be moved to the surviving company.')}
					</li>
					<li>
						{`${oldCompany?.installations_count ?? 0} ` +
							(fields?.merge_companies_confirm_installations ||
								'installation(s) will be moved to the surviving company.')}
					</li>
					<li>
						{`${primaryIndividual?.full_name || '-'} ` +
							(fields?.merge_companies_confirm_primary ||
								'will become the primary individual and company manager.')}
					</li>
				</ul>
				<p className="fw-bold">
					{fields?.merge_companies_confirm_warning ||
						'This action cannot be undone. The merged company will be deleted.'}
				</p>
			</Modal>
		</PageLayout>
	);
}

export default CompanyMerge;
